import * as React from 'react';
import { InjectedIntl, InjectedIntlProps, injectIntl } from 'react-intl';
import JsonLd from '../../../components/common/layouts/JsonLd';
import MetaTags from '../../../components/common/layouts/MetaTags';
import MerchantBookingServicesPage, {
  PageProps,
} from '../../../components/merchants/bookingServices/MerchantBookingServicesPage';
import Area from '../../../domain/entities/Area';
import implCategoryRepository from '../../../infrastructure/repositories/MerchantCategoryRepository';
import implUCMerchantRepository from '../../../infrastructure/repositories/UCMerchantRepository';
import { BookingPageRepositoryForSSR } from '../../../infrastructure/repositories/BookingPageRepository';
import { buildBreadCrumbs } from '../../../utils/breadcrumbs';

const titleBy = (
  intl: InjectedIntl,
  displayName: string,
  currentPage: number,
) => {
  const base = intl.formatMessage(
    { id: 'merchants.bookingPages.index.meta.title' },
    { displayName },
  );
  if (currentPage <= 1) {
    return base;
  }
  return `${base} ${intl.formatMessage(
    { id: 'merchants.bookingPages.index.page' },
    { currentPage },
  )}`;
};

interface Props extends PageProps {
  area?: Area;
}

class MerchantBookingPages extends React.PureComponent<
  Props & InjectedIntlProps
> {
  // tslint:disable-next-line: no-any
  public static async getInitialProps({
    requestId,
    query,
    session,
    token,
  }: any): Promise<Props> {
    const pLayout = implUCMerchantRepository().Layout(query.publicId);

    // page for pagination
    const page = query.page ? query.page : '1';
    const pCategories = implCategoryRepository.Search();
    const pBookingPageList = BookingPageRepositoryForSSR(
      session,
      token,
      requestId,
    ).Search(query.publicId, page);

    return Promise.all([pLayout, pCategories, pBookingPageList]).then(
      results => {
        const layout = results[0];
        const categories = results[1];
        const bookingPageList = results[2];

        return {
          categories,
          category: layout.category,
          merchant: layout.merchant,
          area: layout.area || undefined,
          booking_page_count: layout.bookingServiceCount,
          post_count: layout.blogPostCount,
          product_count: layout.productCount,
          public_id: layout.merchant.publicId,
          bookingPages: bookingPageList.bookingPages,
          pagination: bookingPageList.pagination,
        };
      },
    );
  }

  public render() {
    const {
      merchant,
      area,
      category,
      intl,
      pagination: { currentPage },
    } = this.props;
    const title = titleBy(intl, merchant.displayName, currentPage);
    const breadcrumbs = buildBreadCrumbs(merchant, area, category, {
      path: 'booking_pages',
      text: intl.formatMessage({
        id: 'merchants.nav.bookingPages',
      }),
    });

    return (
      <>
        <MetaTags
          title={title}
          description={merchant.metaSetting.description || merchant.description}
          keywords={merchant.metaSetting.keyword || ''}
          thumbnailUrl={merchant.coverImage.url || ''}
          currentUrl={`/${merchant.publicId}/booking_pages?page=${currentPage}`}
          noIndex={merchant.metaSetting.isNoIndex}
        />
        <MerchantBookingServicesPage
          {...this.props}
          breadcrumbs={breadcrumbs}
        />
        <JsonLd breadcrumbs={breadcrumbs} merchant={merchant} />
      </>
    );
  }
}

export default injectIntl(MerchantBookingPages);
